import React from "react";

import { CartProductOptions } from "./CartProductOptions";
import { CartProductQuantity } from "./CartProductQuantity";
import { mobileBreakpoint } from "../../../../../entrypoints/theme";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import useCurrency from "@src/hooks/useCurrency";

export const CartStackedProduct = ({ item, settings }) => {
  const isMobile = useMobileBreakpoint(mobileBreakpoint);
  const { block_settings } = settings;
  const blockSettings = block_settings || {};

  if (!item) {
    return false;
  }

  // Extract block settings for styling
  const {
    text_color = '#000000',
    secondary_text_color = '#666666',
    price_color = '#000000',
    border_color = '#e0e0e0',
    border_width = 1,
    border_radius = 8,
    item_padding = 16,
    image_size = 91,
    mobile_image_size = 76,
    show_variant_options = true,
    show_compare_at_price = true,
    show_total_price = true,
    remove_icon_size = 16,
    mobile_remove_icon_size = 16
  } = blockSettings;

  const line_item_price = item.price / 100;
  // @ts-ignore
  const { currentCurrencySymbol, currentCurrencyCode } = useCurrency();
  const currencySymbol = currentCurrencySymbol || '$';
  const currencyCode = currentCurrencyCode || 'AUD';

  let totalDiscount = 0;
  if (item.discounts) {
    item.discounts.forEach((discount) => {
      totalDiscount += discount.amount;
    });
  }

  // Calculate if we should show strikethrough
  const hasCompareAtPrice = item.product?.compare_at_price && item.product.compare_at_price > item.price;
  const hasDiscounts = item.discounts && item.discounts.length > 0;
  const shouldShowStrikethrough = show_compare_at_price && (hasCompareAtPrice || hasDiscounts);

  // Inline styles using block settings
  const containerStyle = {
    display: 'flex',
    width: '100%',
    gap: `${item_padding}px`,
    borderBottom: border_width > 0 ? `${border_width}px solid ${border_color}` : 'none',
    borderRadius: `${border_radius}px`,
    padding: `${item_padding}px`,
    color: text_color,
    flexDirection: isMobile ? 'column' : 'row' as const
  };

  const imageStyle = {
    width: isMobile ? `${mobile_image_size}px` : `${image_size}px`,
    height: 'auto',
    flexShrink: 0
  };

  const priceStyle = {
    color: price_color,
    fontWeight: 'normal'
  };

  const compareAtPriceStyle = {
    color: secondary_text_color,
    textDecoration: 'line-through',
    fontWeight: 'normal'
  };

  const removeIconStyle = {
    fontSize: isMobile ? `${mobile_remove_icon_size}px` : `${remove_icon_size}px`,
    cursor: 'pointer',
    color: secondary_text_color
  };

  return (
    <>
      <div style={containerStyle} className="cart__item">
        <div style={{ display: 'flex', gap: `${item_padding}px`, width: '100%' }}>
          <a href={item.url} style={imageStyle}>
            <img
              src={item.image}
              alt={item.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              loading="lazy"
            />
          </a>

          <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <div
              data-ajax-cart-request-button={`/cart/change?id=${item.key}&quantity=0`}
              className="cart__item--remove"
              style={{ 
                display: isMobile ? 'block' : 'none',
                alignSelf: 'flex-end',
                marginBottom: '8px',
                padding: '4px',
                cursor: 'pointer'
              }}
            >
              <i className="icon icon-trash" style={removeIconStyle}></i>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <a href={item.url} style={{ fontWeight: 600, textDecoration: 'none', color: 'inherit' }}>
                  {item.product_title}
                </a>
              </div>

              <div style={{ 
                display: 'flex',
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                gap: '16px',
                flexWrap: 'wrap'
              }}>

                <div className="flex flex-col gap-[16px]" style={{ flex: 1 }}>
                  {show_variant_options && (
                    <div style={{ display: isMobile ? 'none' : 'flex' }}>
                      {CartProductOptions(item.options_with_values.filter(option => 
                        option.value && option.value.toLowerCase() !== 'default title'
                      ))}
                    </div>
                  )}

                  <CartProductQuantity item={item} settings={settings} />
                </div>

                {!isMobile && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                    <div style={{ textAlign: 'right' }}>
                      {show_total_price ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          {shouldShowStrikethrough && hasCompareAtPrice && (
                            <p style={compareAtPriceStyle}>
                              {currencySymbol}{((item.product.compare_at_price * item.quantity) / 100).toFixed(2)}
                            </p>
                          )}
                          {shouldShowStrikethrough && hasDiscounts && !hasCompareAtPrice && (
                            <p style={compareAtPriceStyle}>
                              {currencySymbol}{(item.original_line_price / 100).toFixed(2)}
                            </p>
                          )}
                          <p style={priceStyle}>
                            {currencySymbol}{(line_item_price * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          {shouldShowStrikethrough && hasCompareAtPrice && (
                            <p style={compareAtPriceStyle}>
                              {currencySymbol}{(item.product.compare_at_price / 100).toFixed(2)}
                            </p>
                          )}
                          <p style={priceStyle}>
                            {currencySymbol}{line_item_price.toFixed(2)}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div
                      data-ajax-cart-request-button={`/cart/change?id=${item.key}&quantity=0`}
                      className="cart__item--remove"
                      style={{ 
                        alignSelf: 'flex-end',
                        padding: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <i className="icon icon-trash" style={removeIconStyle}></i>
                    </div>
                  </div>
                )}
              </div>

              <div
                className="flex errors"
                style={{ padding: 0, margin: 0, fontSize: '0.875rem', color: 'red' }}
                data-ajax-cart-errors={`${item.key}`}
              ></div>
            </div>

            <div style={{ marginTop: '8px' }}>
              {item.selling_plan_allocation && (
                <div style={{ 
                  display: 'flex', 
                  gap: '4px', 
                  marginTop: '8px',
                  alignItems: 'center',
                  fontSize: '0.875rem',
                  color: secondary_text_color
                }}>
                  <span>{item.selling_plan_allocation.selling_plan.name}</span>
                </div>
              )}

              {/* Display discounts applied to this line item */}
              <div style={{ marginTop: '4px' }}>
                {item.discounts && item.discounts.length > 0 && item.discounts.map((discount, id) => (
                  <div key={id} style={{ 
                    fontSize: '0.875rem', 
                    color: '#22c55e',
                    margin: '2px 0',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <span style={{ fontSize: '0.75rem' }}>✓</span>
                    <span>{discount.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {isMobile && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {show_variant_options && (
              <div>
                {CartProductOptions(item.options_with_values.filter(option => 
                  option.value && option.value.toLowerCase() !== 'default title'
                ))}
              </div>
            )}
            
            <div style={{ textAlign: 'right' }}>
              {show_total_price ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  {shouldShowStrikethrough && hasCompareAtPrice && (
                    <p style={compareAtPriceStyle}>
                      {currencySymbol}{((item.product.compare_at_price * item.quantity) / 100).toFixed(2)}
                    </p>
                  )}
                  {shouldShowStrikethrough && hasDiscounts && !hasCompareAtPrice && (
                    <p style={compareAtPriceStyle}>
                      {currencySymbol}{(item.original_line_price / 100).toFixed(2)}
                    </p>
                  )}
                  <p style={priceStyle}>
                    {currencySymbol}{(line_item_price * item.quantity).toFixed(2)}
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  {shouldShowStrikethrough && hasCompareAtPrice && (
                    <p style={compareAtPriceStyle}>
                      {currencySymbol}{(item.product.compare_at_price / 100).toFixed(2)}
                    </p>
                  )}
                  <p style={priceStyle}>
                    {currencySymbol}{line_item_price.toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
